import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Image,
  Alert,
  Modal,
  FlatList,
  SafeAreaView,
  RefreshControl,
  Dimensions,
  StyleSheet,
  Switch,
  ActivityIndicator,
  StatusBar
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';

const { width, height } = Dimensions.get('window');

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (e) {}
  return config;
}, (e) => Promise.reject(e));

const getBusinessId = async () => {
  let id = await AsyncStorage.getItem('company_id');
  if (!id) id = await AsyncStorage.getItem('company_id');
  if (!id) {
    try {
      const p = await AsyncStorage.getItem('company_profile');
      if (p) { const parsed = JSON.parse(p); id = parsed?.id ? String(parsed.id) : null; }
    } catch {}
  }
  return id || null;
};

const applyLogoUri = (l) => {
  if (!l || typeof l !== 'string') return null;
  if (l.startsWith('http') || l.startsWith('data:')) return l;
  if (l.startsWith('/')) return `${API_BASE_URL}${l}`;
  return null;
};

const AUTHORIZED_ROLES = ['admin', 'hr_manager', 'hr', 'owner', 'super_admin', 'director', 'ceo', 'cto', 'coo', 'manager'];

const AdminHRManagementScreen = ({ navigation, route }) => {
  // Route params
  const { companyProfile: routeCompanyProfile, companyId: routeCompanyId } = route?.params || {};

  // ── Auth state ──
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [enteredPassword, setEnteredPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMode, setPasswordMode] = useState('enter'); // 'enter' | 'setup' | 'change'
  const [passwordError, setPasswordError] = useState('');
  const [passwordAttempts, setPasswordAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Company state
  const [companyId, setCompanyId] = useState(routeCompanyId || null);
  const [companyProfile, setCompanyProfile] = useState(routeCompanyProfile || null);
  const [companyLogo, setCompanyLogo] = useState(
    applyLogoUri(routeCompanyProfile?.logo || routeCompanyProfile?.logo_url || routeCompanyProfile?.company_logo) || null
  );
  const [companyName, setCompanyName] = useState(
    routeCompanyProfile?.company_name || routeCompanyProfile?.name || ''
  );
  const [isLoading, setIsLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('dashboard');

  // ── HR Permissions (controls what HR Dashboard users can do) ──
  const [hrPermissions, setHrPermissions] = useState({
    add_employee: true,
    edit_employee: true,
    delete_employee: false,
    approve_leave: true,
    reject_leave: true,
    process_payroll: false,
    view_payroll: true,
    post_job: true,
    edit_job: true,
    issue_warning: false,
    terminate_employee: false,
    schedule_review: true,
    create_onboarding: true,
    create_benefit: true,
    create_policy: true,
    bulk_actions: false,
    export_reports: true,
    view_financials: true,
    manage_training: true,
    view_analytics: true,
    create_exit_interview: true,
    full_employee_mgmt: true,
    team_management: true,
    visit_tracking: true,
    follow_up_reminders: true,
    view_employee_details: true,
    navigate_payroll_mgmt: false,
    navigate_recruitment: true,
    create_policy: true,
    resolve_warnings: false,
    delete_warnings: false,
    view_notifications: true,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    department: 'All',
    status: 'All',
    role: 'All'
  });

  const [adminData, setAdminData] = useState({
    dashboard: {
      systemHealth: {
        uptime: '99.8%',
        activeUsers: 1247,
        systemLoad: 67,
        databaseSize: '2.4 GB',
        lastBackup: '2024-10-21 02:00:00',
        criticalAlerts: 2,
        pendingTasks: 15,
        scheduledMaintenance: '2024-10-25 03:00:00'
      },
      recentActivity: [
        {
          id: 'activity_001',
          type: 'user_created',
          description: 'New mentor account created for Dr. Sarah Wilson',
          timestamp: '2024-10-21 14:30:00',
          user: 'admin@company.com',
          severity: 'info'
        },
        {
          id: 'activity_002',
          type: 'system_alert',
          description: 'High memory usage detected on server-02',
          timestamp: '2024-10-21 13:45:00',
          user: 'system',
          severity: 'warning'
        },
        {
          id: 'activity_003',
          type: 'data_export',
          description: 'Quarterly internship report generated',
          timestamp: '2024-10-21 12:15:00',
          user: 'hr.manager@company.com',
          severity: 'info'
        },
        {
          id: 'activity_004',
          type: 'security_event',
          description: 'Failed login attempts from IP 192.168.1.100',
          timestamp: '2024-10-21 11:20:00',
          user: 'security_system',
          severity: 'critical'
        }
      ],
      quickStats: {
        totalUsers: 1247,
        activeInternships: 156,
        pendingApplications: 89,
        systemAlerts: 7,
        dataBackups: 'Current',
        securityStatus: 'Secure'
      }
    },
    systemAlerts: [
      {
        id: 'alert_001',
        title: 'High Memory Usage',
        message: 'Server memory usage is at 89%. Consider scaling resources.',
        severity: 'warning',
        timestamp: '2024-10-21 14:15:00'
      },
      {
        id: 'alert_002',
        title: 'Failed Login Attempts',
        message: 'Multiple failed login attempts detected from IP 192.168.1.100',
        severity: 'critical',
        timestamp: '2024-10-21 13:30:00'
      },
      {
        id: 'alert_003',
        title: 'Backup Completed',
        message: 'Daily backup completed successfully at 02:00 AM',
        severity: 'info',
        timestamp: '2024-10-21 02:00:00'
      }
    ],
    users: [
      {
        id: 'user_001',
        name: 'Dr. Sarah Wilson',
        email: 'sarah.wilson@company.com',
        role: 'Mentor',
        department: 'Engineering',
        status: 'Active',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150',
        phone: '+1 (555) 123-4567',
        location: 'San Francisco, CA',
        joinDate: '2024-01-15 09:00:00',
        lastLogin: '2024-10-21 08:30:00',
        internCount: 5,
        averageRating: 4.8,
        permissions: ['mentor_access', 'evaluation_create', 'intern_view']
      },
      {
        id: 'user_002',
        name: 'Michael Chen',
        email: 'michael.chen@company.com',
        role: 'Intern',
        department: 'Engineering',
        status: 'Active',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
        phone: '+1 (555) 234-5678',
        location: 'New York, NY',
        joinDate: '2024-09-01 09:00:00',
        lastLogin: '2024-10-21 14:15:00',
        progress: 75,
        mentor: 'Dr. Sarah Wilson',
        permissions: ['intern_access', 'resource_view']
      },
      {
        id: 'user_003',
        name: 'Jennifer Rodriguez',
        email: 'jennifer.rodriguez@company.com',
        role: 'HR Manager',
        department: 'HR',
        status: 'Active',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150',
        phone: '+1 (555) 345-6789',
        location: 'Austin, TX',
        joinDate: '2023-03-10 09:00:00',
        lastLogin: '2024-10-21 10:45:00',
        managedPrograms: 12,
        processedApplications: 156,
        permissions: ['hr_access', 'user_create', 'report_generate', 'program_manage']
      }
    ],
    systemSettings: {
      general: {
        companyName: 'TechCorp Inc.',
        systemTimezone: 'UTC-8 (PST)',
        defaultLanguage: 'English (US)',
        maintenanceMode: false,
        autoBackup: true,
        dataRetention: '7 years'
      },
      security: {
        passwordPolicy: 'Strong',
        twoFactorAuth: true,
        sessionTimeout: 30,
        maxLoginAttempts: 5,
        auditLogging: true
      },
      notifications: {
        emailNotifications: true,
        pushNotifications: true,
        smsNotifications: false,
        slackIntegration: true
      },
      integrations: {
        googleWorkspace: {
          enabled: true,
          domain: 'techcorp.com',
          adminEmail: 'admin@techcorp.com'
        },
        microsoftTeams: {
          enabled: false,
          tenantId: '',
          clientId: ''
        },
        slack: {
          enabled: true,
          workspaceUrl: 'techcorp.slack.com',
          botToken: 'xoxb-...'
        },
        zoom: {
          enabled: true,
          apiKey: 'zoom_api_key',
          apiSecret: 'zoom_api_secret'
        }
      }
    },
    reports: {
      scheduled: [
        {
          id: 'report_001',
          name: 'Weekly Internship Summary',
          description: 'Comprehensive weekly report on internship activities',
          frequency: 'Weekly',
          nextRun: '2024-10-28 09:00:00',
          recipients: ['hr@company.com', 'management@company.com'],
          format: 'PDF',
          status: 'Active',
          lastRun: '2024-10-21 09:00:00',
          lastRunStatus: 'Success'
        },
        {
          id: 'report_002',
          name: 'Monthly Performance Analytics',
          description: 'Detailed performance metrics and analytics',
          frequency: 'Monthly',
          nextRun: '2024-11-01 08:00:00',
          recipients: ['analytics@company.com'],
          format: 'Excel',
          status: 'Active',
          lastRun: '2024-10-01 08:00:00',
          lastRunStatus: 'Success'
        }
      ],
      custom: [
        {
          id: 'custom_001',
          name: 'Q3 Diversity Report',
          description: 'Quarterly diversity and inclusion metrics',
          createdBy: 'hr.manager@company.com',
          createdDate: '2024-10-15',
          parameters: {
            dateRange: 'Q3 2024',
            includeGraphics: true,
            breakdownBy: ['department', 'role', 'location']
          },
          status: 'Completed',
          downloadUrl: '/reports/q3-diversity-2024.pdf'
        }
      ]
    },
    auditLogs: [
      {
        id: 'audit_001',
        timestamp: '2024-10-21 14:30:00',
        user: 'admin@company.com',
        action: 'USER_CREATED',
        resource: 'User: sarah.wilson@company.com',
        details: 'Created new mentor account with engineering department access',
        ipAddress: '192.168.1.50',
        userAgent: 'Mozilla/5.0...',
        severity: 'INFO'
      },
      {
        id: 'audit_002',
        timestamp: '2024-10-21 13:45:00',
        user: 'hr.manager@company.com',
        action: 'REPORT_GENERATED',
        resource: 'Report: Weekly Summary',
        details: 'Generated weekly internship summary report',
        ipAddress: '192.168.1.45',
        userAgent: 'Mozilla/5.0...',
        severity: 'INFO'
      }
    ]
  });

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'Intern',
    department: '',
    permissions: [],
    phone: '',
    location: ''
  });

  useEffect(() => { checkAuthAndInit(); }, []);

  // ── Authorization Check ──
  const checkAuthAndInit = async () => {
    setAuthChecking(true);
    try {
      // 1. Check auth token exists
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      if (!token) {
        setIsAuthorized(false);
        setAuthChecking(false);
        return;
      }

      // 2. Load user data from storage
      let user = null;
      const userData = await AsyncStorage.getItem('user_data');
      if (userData) {
        try { user = JSON.parse(userData); } catch {}
      }

      // 3. Try API verification
      const userId = await AsyncStorage.getItem('user_id');
      if (userId) {
        try {
          const res = await api.get(`/api/users/${userId}/profile`);
          const apiUser = res.data?.data?.[0] || res.data?.data || res.data;
          if (apiUser) user = { ...user, ...apiUser };
        } catch (e) { console.warn('User profile API:', e?.message); }
      }

      if (!user) {
        // No user data at all — try route params
        user = route?.params?.user || route?.params?.userData || null;
      }

      setCurrentUser(user);

      // 4. Check role authorization
      if (user) {
        const userRole = (user.user_role || user.role || user.account_type || user.type || '').toLowerCase().trim();
        const isAdmin = AUTHORIZED_ROLES.some(r => userRole.includes(r));
        
        // Also check if user is the business owner
        const bizId = routeCompanyId || await getBusinessId();
        const isOwner = user.company_id && String(user.company_id) === String(bizId);
        const hasAdminFlag = user.is_admin === true || user.is_admin === 1 || user.admin === true;

        setIsAuthorized(isAdmin || isOwner || hasAdminFlag);
      } else {
        setIsAuthorized(false);
      }
    } catch (e) {
      console.error('Auth check error:', e);
      setIsAuthorized(false);
    } finally {
      setAuthChecking(false);
    }

    // Continue — check password gate
    await checkPasswordGate();
  };

  // ── Password Gate (DB: admin_passwords, screen_name based) ──
  const MAX_ATTEMPTS = 5;
  const LOCKOUT_MINUTES = 15;
  const API_PW = 'https://api.jobeng.ai/api/data/admin_passwords';
  const OWN_SCREEN_NAME = 'admin_hr';

  const SCREEN_LABELS = {
    admin_hr: 'Admin Panel',
    hr_dashboard: 'HR Dashboard',
    employee_management: 'Employee Management',
    payroll_management: 'Payroll Management',
    global: 'Global (all screens)',
  };

  const getPassHeaders = async () => {
    const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    return { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
  };

  // Generic: get password record for a specific screen
  const getPasswordRecord = async (screenName) => {
    const bizId = companyId || routeCompanyId || await getBusinessId();
    if (!bizId) return null;
    try {
      const headers = await getPassHeaders();
      const res = await fetch(`${API_PW}?company_id=${bizId}&screen_name=${screenName}`, { headers });
      if (!res.ok) return null;
      const json = await res.json();
      const recs = (json?.data || []).filter(r => r.screen_name === screenName);
      return recs.length > 0 ? recs[0] : null;
    } catch { return null; }
  };

  // Generic: set password for any screen
  const setScreenSpecificPassword = async (screenName, password) => {
    const bizId = companyId || routeCompanyId || await getBusinessId();
    if (!bizId) return { success: false, error: 'No business ID' };
    try {
      const headers = await getPassHeaders();
      const existing = await getPasswordRecord(screenName);
      if (existing) {
        await fetch(`${API_PW}/${existing.id}`, { method: 'PUT', headers, body: JSON.stringify({ password_hash: password, attempts: 0, locked_until: null }) });
      } else {
        await fetch(API_PW, { method: 'POST', headers, body: JSON.stringify({ company_id: parseInt(bizId), screen_name: screenName, password_hash: password, password_salt: 'default_salt', attempts: 0, locked_until: null }) });
      }
      return { success: true };
    } catch (e) { return { success: false, error: e?.message }; }
  };

  // Generic: remove password for a screen
  const removeScreenPassword = async (screenName) => {
    try {
      const rec = await getPasswordRecord(screenName);
      if (rec) { const headers = await getPassHeaders(); await fetch(`${API_PW}/${rec.id}`, { method: 'DELETE', headers }); }
      return { success: true };
    } catch (e) { return { success: false, error: e?.message }; }
  };

  // ── Own screen gate (admin_hr) ──
  const checkPasswordGate = async () => {
    try {
      const bizId = companyId || routeCompanyId || await getBusinessId();
      if (!bizId) { setHasPassword(false); setPasswordMode('setup'); return; }
      // Check own screen password, then global fallback
      let rec = await getPasswordRecord(OWN_SCREEN_NAME);
      if (!rec) rec = await getPasswordRecord('global');
      if (rec) {
        setHasPassword(true);
        setPasswordMode('enter');
        if (rec.locked_until && new Date(rec.locked_until) > new Date()) setLockoutUntil(new Date(rec.locked_until).getTime());
      } else {
        setHasPassword(false);
        setPasswordMode('setup');
      }
    } catch { setPasswordMode('setup'); }
  };

  const verifyPassword = async () => {
    setPasswordError('');
    if (lockoutUntil && Date.now() < lockoutUntil) {
      setPasswordError(`Locked. Try again in ${Math.ceil((lockoutUntil - Date.now()) / 60000)} min.`); return;
    }
    if (!enteredPassword || enteredPassword.length < 4) { setPasswordError('Min 4 characters'); return; }
    try {
      let rec = await getPasswordRecord(OWN_SCREEN_NAME);
      if (!rec) rec = await getPasswordRecord('global');
      if (!rec) { setPasswordError('No password found.'); return; }
      if (rec.locked_until && new Date(rec.locked_until) > new Date()) {
        setLockoutUntil(new Date(rec.locked_until).getTime()); setPasswordError('Account locked.'); return;
      }
      if (enteredPassword === rec.password_hash) {
        setPasswordVerified(true); setPasswordAttempts(0); setEnteredPassword('');
        const headers = await getPassHeaders();
        try { await fetch(`${API_PW}/${rec.id}`, { method: 'PUT', headers, body: JSON.stringify({ attempts: 0, locked_until: null }) }); } catch {}
        await initScreen();
      } else {
        const a = (rec.attempts || 0) + 1; setPasswordAttempts(a);
        let lu = null;
        if (a >= MAX_ATTEMPTS) { lu = new Date(Date.now() + LOCKOUT_MINUTES * 60 * 1000).toISOString(); setLockoutUntil(Date.now() + LOCKOUT_MINUTES * 60 * 1000); setPasswordError(`Locked for ${LOCKOUT_MINUTES} min.`); }
        else { setPasswordError(`Incorrect. ${MAX_ATTEMPTS - a} left.`); }
        const headers = await getPassHeaders();
        try { await fetch(`${API_PW}/${rec.id}`, { method: 'PUT', headers, body: JSON.stringify({ attempts: a, locked_until: lu }) }); } catch {}
      }
    } catch { setPasswordError('Verification failed.'); }
  };

  const setupPassword = async () => {
    setPasswordError('');
    if (!newPassword || newPassword.length < 4) { setPasswordError('Min 4 characters'); return; }
    if (newPassword.length > 20) { setPasswordError('Max 20 characters'); return; }
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return; }
    const result = await setScreenSpecificPassword(OWN_SCREEN_NAME, newPassword);
    if (result.success) {
      setHasPassword(true); setPasswordVerified(true); setNewPassword(''); setConfirmPassword('');
      Alert.alert('Password Set', 'Admin panel password configured.');
      await initScreen();
    } else { setPasswordError('Failed: ' + (result.error || '')); }
  };

  const changePassword = async () => {
    setPasswordError('');
    const rec = await getPasswordRecord(OWN_SCREEN_NAME);
    if (!rec) { const grec = await getPasswordRecord('global'); if (!grec) { setPasswordError('No password found'); return; } if (enteredPassword !== grec.password_hash) { setPasswordError('Current password is incorrect'); return; } }
    else if (enteredPassword !== rec.password_hash) { setPasswordError('Current password is incorrect'); return; }
    if (!newPassword || newPassword.length < 4) { setPasswordError('Min 4 characters'); return; }
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return; }
    const result = await setScreenSpecificPassword(OWN_SCREEN_NAME, newPassword);
    if (result.success) { setEnteredPassword(''); setNewPassword(''); setConfirmPassword(''); setPasswordMode('enter'); Alert.alert('Changed', 'Password updated.'); }
    else { setPasswordError('Failed: ' + (result.error || '')); }
  };

  const resetPassword = () => {
    Alert.alert('Reset Admin Password', 'Remove password for this screen?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Reset', style: 'destructive', onPress: async () => {
        const result = await removeScreenPassword(OWN_SCREEN_NAME);
        if (result.success) {
          setHasPassword(false); setPasswordVerified(false); setPasswordMode('setup');
          setPasswordAttempts(0); setLockoutUntil(null); setEnteredPassword(''); setNewPassword(''); setConfirmPassword('');
          Alert.alert('Reset', 'Set a new password.');
        } else { Alert.alert('Error', 'Reset failed.'); }
      }},
    ]);
  };

  // ── Per-screen password management (called from Settings UI) ──
  const [screenPasswords, setScreenPasswords] = useState({});
  const [screenPassInput, setScreenPassInput] = useState('');
  const [screenPassConfirm, setScreenPassConfirm] = useState('');
  const [activeScreenPass, setActiveScreenPass] = useState(null);

  const loadAllScreenPasswords = async () => {
    const bizId = companyId || routeCompanyId || await getBusinessId();
    if (!bizId) return;
    const result = {};
    for (const sn of Object.keys(SCREEN_LABELS)) {
      const rec = await getPasswordRecord(sn);
      result[sn] = rec ? { exists: true, id: rec.id } : { exists: false };
    }
    setScreenPasswords(result);
  };

  const handleSetScreenPassword = async (screenName) => {
    if (!screenPassInput || screenPassInput.length < 4) { Alert.alert('Error', 'Min 4 characters'); return; }
    if (screenPassInput !== screenPassConfirm) { Alert.alert('Error', 'Passwords do not match'); return; }
    const result = await setScreenSpecificPassword(screenName, screenPassInput);
    if (result.success) {
      Alert.alert('Set', `Password for ${SCREEN_LABELS[screenName]} has been set.`);
      setScreenPassInput(''); setScreenPassConfirm(''); setActiveScreenPass(null);
      loadAllScreenPasswords();
    } else { Alert.alert('Error', result.error || 'Failed'); }
  };

  const handleRemoveScreenPassword = (screenName) => {
    Alert.alert('Remove Password', `Remove password for ${SCREEN_LABELS[screenName]}?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: async () => {
        const result = await removeScreenPassword(screenName);
        if (result.success) { Alert.alert('Removed', 'Password removed.'); loadAllScreenPasswords(); }
        else { Alert.alert('Error', 'Failed'); }
      }},
    ]);
  };

  const initScreen = async () => {
    setIsLoading(true);
    try {
      await loadCompanyProfile();
      await loadHRPermissions();
      await loadAllScreenPasswords();
      await loadAdminData();
    } catch (e) { console.error('Init error:', e); }
    finally { setIsLoading(false); }
  };

  const loadHRPermissions = async () => {
    try {
      const bizId = companyId || routeCompanyId || await getBusinessId();
      const key = `hr_permissions_${bizId || 'default'}`;
      const cached = await AsyncStorage.getItem(key);
      if (cached) setHrPermissions(JSON.parse(cached));
      // Try API
      if (bizId) {
        try {
          const res = await api.get('/api/data/hr_permissions', { params: { company_id: parseInt(bizId), limit: 1 } });
          if (res.data?.success && res.data.data?.[0]) {
            const perms = res.data.data[0];
            delete perms.id; delete perms.company_id; delete perms.created_at; delete perms.updated_at;
            setHrPermissions(prev => ({ ...prev, ...perms }));
            await AsyncStorage.setItem(key, JSON.stringify({ ...hrPermissions, ...perms }));
          }
        } catch {}
      }
    } catch {}
  };

  const saveHRPermissions = async (newPerms) => {
    setHrPermissions(newPerms);
    const bizId = companyId || await getBusinessId();
    const key = `hr_permissions_${bizId || 'default'}`;
    await AsyncStorage.setItem(key, JSON.stringify(newPerms));
    // Sync to API
    if (bizId) {
      try {
        await api.post('/api/data/hr_permissions', { ...newPerms, company_id: parseInt(bizId) });
      } catch {}
    }
    Alert.alert('Saved', 'HR permissions updated. Changes take effect immediately for all HR users.');
  };

  const togglePermission = (key) => {
    const updated = { ...hrPermissions, [key]: !hrPermissions[key] };
    saveHRPermissions(updated);
  };

  // ── Company Profile ──
  const loadCompanyProfile = async () => {
    try {
      const bizId = routeCompanyId || companyId || await getBusinessId();
      if (bizId && !companyId) setCompanyId(bizId);

      if (routeCompanyProfile) {
        setCompanyProfile(routeCompanyProfile);
        setCompanyName(routeCompanyProfile.company_name || routeCompanyProfile.name || '');
        setCompanyLogo(applyLogoUri(routeCompanyProfile.logo || routeCompanyProfile.logo_url || routeCompanyProfile.company_logo || routeCompanyProfile.banner_image_url));
      }

      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const cp = JSON.parse(cached);
        if (!companyName) setCompanyName(cp.company_name || cp.name || '');
        if (!companyLogo) setCompanyLogo(applyLogoUri(cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url));
        if (!companyProfile) setCompanyProfile(cp);
      }

      if (bizId) {
        try {
          const res = await api.get(`/api/company-profile/${parseInt(bizId) || 0}`);
          if (res.data?.success && res.data.data) {
            const raw = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
            if (raw) {
              setCompanyProfile(raw);
              setCompanyName(raw.name || raw.company_name || '');
              setCompanyLogo(applyLogoUri(raw.logo || raw.logo_url || raw.company_logo || raw.banner_image_url));
              await AsyncStorage.setItem('company_profile', JSON.stringify(raw));
            }
          }
        } catch (e) { console.warn('Company profile API:', e?.message); }
      }
    } catch {}
  };

  // ── Load Admin Data (API → cache → defaults) ──
  const loadAdminData = async () => {
    const bizId = companyId || routeCompanyId || await getBusinessId();
    const bId = parseInt(bizId) || 0;

    try {
      // Fetch live stats from multiple API endpoints
      const safeGet = (url, params) => api.get(url, { params }).catch(() => ({ data: { success: false } }));
      const [empRes, projRes, clientRes, teamRes, leaveRes] = await Promise.allSettled([
        safeGet('/api/employees', { company_id: bId, limit: 500 }),
        safeGet('/api/projects', { company_id: bId, limit: 500 }),
        safeGet('/api/clients', { company_id: bId, limit: 500 }),
        safeGet('/api/team/members', { company_id: bId }),
        safeGet('/api/data/hr_leave_requests', { company_id: bId, limit: 100 }),
      ]);

      const ext = (r) => (r.status === 'fulfilled' && r.value.data?.success ? r.value.data.data : []);
      const employees = Array.isArray(ext(empRes)) ? ext(empRes) : [];
      const projects = Array.isArray(ext(projRes)) ? ext(projRes) : [];
      const clients = Array.isArray(ext(clientRes)) ? ext(clientRes) : [];
      const team = Array.isArray(ext(teamRes)) ? ext(teamRes) : [];
      const leaves = Array.isArray(ext(leaveRes)) ? ext(leaveRes) : [];

      const activeEmps = employees.filter(e => e.status === 'active' || !e.status).length;

      // Merge API data into dashboard
      setAdminData(prev => ({
        ...prev,
        dashboard: {
          ...prev.dashboard,
          quickStats: {
            ...prev.dashboard.quickStats,
            totalUsers: employees.length || team.length || prev.dashboard.quickStats.totalUsers,
            activeInternships: projects.filter(p => p.status === 'active').length || prev.dashboard.quickStats.activeInternships,
            pendingApplications: leaves.filter(l => l.status === 'pending').length || prev.dashboard.quickStats.pendingApplications,
          },
          systemHealth: {
            ...prev.dashboard.systemHealth,
            activeUsers: activeEmps || prev.dashboard.systemHealth.activeUsers,
          },
        },
        // Populate users from employees + team
        users: (employees.length > 0 ? employees : team).map((e, i) => ({
          id: String(e.id || `user_${i}`),
          name: e.full_name || e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'Employee',
          email: e.email || e.work_email || '',
          role: e.role || e.job_title || e.position || 'Staff',
          department: e.department || 'General',
          status: e.status || 'Active',
          lastActive: e.updated_at || e.last_login || e.created_at || '',
          phone: e.phone || e.mobile || '',
          joinDate: e.hire_date || e.start_date || e.created_at || '',
        })).concat(prev.users.length > 0 && employees.length === 0 && team.length === 0 ? prev.users : []),
      }));

      // Cache
      await AsyncStorage.setItem(`adminData_${bizId}`, JSON.stringify(adminData));

      console.log(`✅ Admin data loaded: ${employees.length} employees, ${projects.length} projects, ${clients.length} clients`);
    } catch (error) {
      console.warn('loadAdminData API error:', error?.message);
      // Fallback: try cache
      try {
        const cached = await AsyncStorage.getItem(`adminData_${bizId}`) || await AsyncStorage.getItem('adminData');
        if (cached) setAdminData(JSON.parse(cached));
      } catch {}
    }
  };

  const saveAdminData = async (data) => {
    try {
      const bizId = companyId || await getBusinessId();
      await AsyncStorage.setItem(`adminData_${bizId || 'default'}`, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving admin data:', error);
    }
  };

  const onRefresh = React.useCallback(async () => {
    setRefreshing(true);
    await loadAdminData();
    setRefreshing(false);
  }, [companyId]);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return '#10B981';
      case 'inactive': return '#8E8E93';
      case 'suspended': return '#EF4444';
      case 'pending': return '#F59E0B';
      default: return '#8E8E93';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical': return '#EF4444';
      case 'warning': return '#F59E0B';
      case 'info': return '#667eea';
      case 'success': return '#10B981';
      default: return '#8E8E93';
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredUsers = () => {
    return adminData.users.filter(user => {
      const matchesSearch = searchQuery === '' || 
        String(user.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(user.email ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(user.department ?? '').toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesDepartment = filters.department === 'All' || user.department === filters.department;
      const matchesStatus = filters.status === 'All' || user.status === filters.status;
      const matchesRole = filters.role === 'All' || user.role === filters.role;
      
      return matchesSearch && matchesDepartment && matchesStatus && matchesRole;
    });
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#667eea', '#764ba2']}
      style={styles.header}
    >
      <SafeAreaView>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View style={styles.headerTitleContainer}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              {companyLogo ? (
                <Image source={{ uri: companyLogo || undefined }} style={{ width: 30, height: 30, borderRadius: 15, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.4)' }} />
              ) : null}
              <View>
                <Text style={styles.headerTitle}>Admin & HR</Text>
                <Text style={styles.headerSubtitle}>{companyName || 'System Management'}</Text>
              </View>
            </View>
          </View>
          
          <TouchableOpacity
            style={styles.alertButton}
            onPress={() => {
              setModalType('systemAlerts');
              setShowModal(true);
            }}
          >
            <Ionicons name="notifications" size={24} color="#FFFFFF" />
            {adminData.dashboard.systemHealth.criticalAlerts > 0 && (
              <View style={styles.alertBadge}>
                <Text style={styles.alertBadgeText}>
                  {adminData.dashboard.systemHealth.criticalAlerts}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 4 }}>
      {[
        { key: 'dashboard', title: 'Dashboard', icon: 'speedometer' },
        { key: 'users', title: 'Users', icon: 'people' },
        { key: 'hr', title: 'HR', icon: 'briefcase' },
        { key: 'settings', title: 'Settings', icon: 'settings' },
        { key: 'reports', title: 'Reports', icon: 'document-text' },
        { key: 'audit', title: 'Audit', icon: 'shield-checkmark' }
      ].map((tab) => (
        <TouchableOpacity
          key={tab.key}
          style={[styles.tabItem, activeTab === tab.key && styles.activeTab]}
          onPress={() => setActiveTab(tab.key)}
        >
          <Ionicons 
            name={tab.icon} 
            size={18} 
            color={activeTab === tab.key ? '#667eea' : '#8E8E93'} 
          />
          <Text style={[
            styles.tabText, 
            activeTab === tab.key && styles.activeTabText
          ]}>
            {tab.title}
          </Text>
        </TouchableOpacity>
      ))}
      </ScrollView>
    </View>
  );

  const renderSystemHealthCard = () => (
    <View style={styles.systemHealthCard}>
      <Text style={styles.sectionTitle}>System Health</Text>
      <View style={styles.healthMetricsGrid}>
        <View style={styles.healthMetric}>
          <MaterialCommunityIcons name="server" size={24} color="#10B981" />
          <Text style={styles.healthMetricValue}>{adminData.dashboard.systemHealth.uptime}</Text>
          <Text style={styles.healthMetricLabel}>Uptime</Text>
        </View>
        <View style={styles.healthMetric}>
          <MaterialCommunityIcons name="account-group" size={24} color="#667eea" />
          <Text style={styles.healthMetricValue}>{adminData.dashboard.systemHealth.activeUsers}</Text>
          <Text style={styles.healthMetricLabel}>Active Users</Text>
        </View>
        <View style={styles.healthMetric}>
          <MaterialCommunityIcons name="memory" size={24} color="#F59E0B" />
          <Text style={styles.healthMetricValue}>{adminData.dashboard.systemHealth.systemLoad}%</Text>
          <Text style={styles.healthMetricLabel}>System Load</Text>
        </View>
        <View style={styles.healthMetric}>
          <MaterialCommunityIcons name="database" size={24} color="#EF4444" />
          <Text style={styles.healthMetricValue}>{adminData.dashboard.systemHealth.databaseSize}</Text>
          <Text style={styles.healthMetricLabel}>Database</Text>
        </View>
      </View>
      
      <View style={styles.healthDetails}>
        <View style={styles.healthDetailItem}>
          <MaterialCommunityIcons name="backup-restore" size={16} color="#8E8E93" />
          <Text style={styles.healthDetailText}>
            Last Backup: {formatDateTime(adminData.dashboard.systemHealth.lastBackup)}
          </Text>
        </View>
        <View style={styles.healthDetailItem}>
          <MaterialCommunityIcons name="wrench" size={16} color="#8E8E93" />
          <Text style={styles.healthDetailText}>
            Next Maintenance: {formatDateTime(adminData.dashboard.systemHealth.scheduledMaintenance)}
          </Text>
        </View>
      </View>
    </View>
  );

  const renderQuickStatsCard = () => (
    <View style={styles.quickStatsCard}>
      <Text style={styles.sectionTitle}>Quick Stats</Text>
      <View style={styles.quickStatsGrid}>
        <TouchableOpacity style={styles.quickStat}>
          <MaterialCommunityIcons name="account-multiple" size={20} color="#667eea" />
          <Text style={styles.quickStatValue}>{adminData.dashboard.quickStats.totalUsers}</Text>
          <Text style={styles.quickStatLabel}>Total Users</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickStat}>
          <MaterialCommunityIcons name="school" size={20} color="#10B981" />
          <Text style={styles.quickStatValue}>{adminData.dashboard.quickStats.activeInternships}</Text>
          <Text style={styles.quickStatLabel}>Active Internships</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickStat}>
          <MaterialCommunityIcons name="file-document" size={20} color="#F59E0B" />
          <Text style={styles.quickStatValue}>{adminData.dashboard.quickStats.pendingApplications}</Text>
          <Text style={styles.quickStatLabel}>Pending Apps</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickStat}>
          <MaterialCommunityIcons name="alert" size={20} color="#EF4444" />
          <Text style={styles.quickStatValue}>{adminData.dashboard.quickStats.systemAlerts}</Text>
          <Text style={styles.quickStatLabel}>System Alerts</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderRecentActivityCard = () => (
    <View style={styles.recentActivityCard}>
      <View style={styles.activityHeader}>
        <Text style={styles.sectionTitle}>Recent Activity</Text>
        <TouchableOpacity
          style={styles.viewAllButton}
          onPress={() => {
            setModalType('allActivity');
            setShowModal(true);
          }}
        >
          <Text style={styles.viewAllButtonText}>View All</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.activityList}>
        {adminData.dashboard.recentActivity.slice(0, 4).map((activity) => (
          <View key={activity.id} style={styles.activityItem}>
            <View style={[
              styles.activityIcon,
              { backgroundColor: getSeverityColor(activity.severity) + '20' }
            ]}>
              <MaterialCommunityIcons 
                name={
                  activity.type === 'user_created' ? 'account-plus' :
                  activity.type === 'system_alert' ? 'alert' :
                  activity.type === 'data_export' ? 'download' :
                  activity.type === 'security_event' ? 'shield-alert' : 'information'
                } 
                size={16} 
                color={getSeverityColor(activity.severity)} 
              />
            </View>
            <View style={styles.activityContent}>
              <Text style={styles.activityDescription}>{activity.description}</Text>
              <Text style={styles.activityTimestamp}>
                {formatDateTime(activity.timestamp)} • {activity.user}
              </Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );

  const renderDashboard = () => (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
      {renderSystemHealthCard()}
      {renderQuickStatsCard()}
      {renderRecentActivityCard()}
    </ScrollView>
  );

  const renderUserCard = ({ item }) => (
    <TouchableOpacity
      style={styles.userCard}
      onPress={() => {
        setSelectedItem(item);
        setModalType('userDetails');
        setShowModal(true);
      }}
    >
      <View style={styles.userHeader}>
        <Image source={{ uri: (item.avatar && item.avatar.startsWith('http')) ? item.avatar : undefined }} style={styles.userAvatar} />
        <View style={styles.userInfo}>
          <Text style={styles.userName}>{item.name}</Text>
          <Text style={styles.userEmail}>{item.email}</Text>
          <View style={styles.userMeta}>
            <Text style={styles.userRole}>{item.role}</Text>
            <Text style={styles.userDepartment}> • {item.department}</Text>
          </View>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) + '20' }]}>
          <Text style={[styles.statusText, { color: getStatusColor(item.status) }]}>
            {item.status}
          </Text>
        </View>
      </View>

      <View style={styles.userStats}>
        {item.role === 'Mentor' && (
          <>
            <View style={styles.userStat}>
              <MaterialCommunityIcons name="account-group" size={16} color="#667eea" />
              <Text style={styles.userStatText}>{item.internCount} interns</Text>
            </View>
            <View style={styles.userStat}>
              <MaterialCommunityIcons name="star" size={16} color="#F59E0B" />
              <Text style={styles.userStatText}>{item.averageRating} rating</Text>
            </View>
          </>
        )}
        {item.role === 'Intern' && (
          <>
            <View style={styles.userStat}>
              <MaterialCommunityIcons name="progress-check" size={16} color="#10B981" />
              <Text style={styles.userStatText}>{item.progress}% complete</Text>
            </View>
            <View style={styles.userStat}>
              <MaterialCommunityIcons name="account-tie" size={16} color="#667eea" />
              <Text style={styles.userStatText}>{item.mentor}</Text>
            </View>
          </>
        )}
        {item.role === 'HR Manager' && (
          <>
            <View style={styles.userStat}>
              <MaterialCommunityIcons name="school" size={16} color="#10B981" />
              <Text style={styles.userStatText}>{item.managedPrograms} programs</Text>
            </View>
            <View style={styles.userStat}>
              <MaterialCommunityIcons name="file-document" size={16} color="#F59E0B" />
              <Text style={styles.userStatText}>{item.processedApplications} apps</Text>
            </View>
          </>
        )}
      </View>

      <View style={styles.userFooter}>
        <View style={styles.userLocation}>
          <MaterialCommunityIcons name="map-marker" size={14} color="#8E8E93" />
          <Text style={styles.userLocationText}>{item.location}</Text>
        </View>
        <Text style={styles.userLastLogin}>
          Last login: {formatDateTime(item.lastLogin)}
        </Text>
      </View>
    </TouchableOpacity>
  );

  const renderUsers = () => (
    <View style={styles.content}>
      <View style={styles.usersHeader}>
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search" size={20} color="#8E8E93" />
            <TextInput
              style={styles.searchInput}
              placeholder="Search users..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor="#8E8E93"
            />
          </View>
          <TouchableOpacity
            style={styles.filterButton}
            onPress={() => {
              setModalType('userFilters');
              setShowModal(true);
            }}
          >
            <Ionicons name="filter" size={20} color="#667eea" />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.addUserButton}
            onPress={() => {
              setModalType('createUser');
              setShowModal(true);
            }}
          >
            <Ionicons name="add" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>

      <FlatList
        data={getFilteredUsers()}
        keyExtractor={(item) => item.id}
        renderItem={renderUserCard}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={() => (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="account-search" size={64} color="#C7C7CC" />
            <Text style={styles.emptyStateTitle}>No Users Found</Text>
            <Text style={styles.emptyStateText}>
              Try adjusting your search or filters
            </Text>
          </View>
        )}
      />
    </View>
  );

  const renderSettingsSection = (title, items) => (
    <View style={styles.settingsSection}>
      <Text style={styles.settingsSectionTitle}>{title}</Text>
      <View style={styles.settingsItems}>
        {items.map((item, index) => (
          <TouchableOpacity key={index} style={styles.settingsItem}>
            <View style={styles.settingsItemLeft}>
              <MaterialCommunityIcons name={item.icon} size={20} color="#667eea" />
              <View style={styles.settingsItemInfo}>
                <Text style={styles.settingsItemTitle}>{item.title}</Text>
                {item.description && (
                  <Text style={styles.settingsItemDescription}>{item.description}</Text>
                )}
              </View>
            </View>
            <View style={styles.settingsItemRight}>
              {item.type === 'toggle' ? (
                <Switch
                  value={item.value}
                  onValueChange={(value) => {
                    // Handle toggle change
                    console.log(`Toggle ${item.title}: ${value}`);
                  }}
                  trackColor={{ false: '#E5E5EA', true: '#667eea' }}
                  thumbColor="#FFFFFF"
                />
              ) : item.type === 'value' ? (
                <Text style={styles.settingsItemValue}>{item.value}</Text>
              ) : (
                <Ionicons name="chevron-forward" size={16} color="#8E8E93" />
              )}
            </View>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderSettings = () => (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
      {renderSettingsSection('General', [
        {
          icon: 'domain',
          title: 'Company Name',
          description: 'Organization display name',
          type: 'value',
          value: adminData.systemSettings.general.companyName
        },
        {
          icon: 'clock',
          title: 'System Timezone',
          description: 'Default timezone for the system',
          type: 'value',
          value: adminData.systemSettings.general.systemTimezone
        },
        {
          icon: 'translate',
          title: 'Default Language',
          description: 'System default language',
          type: 'value',
          value: adminData.systemSettings.general.defaultLanguage
        },
        {
          icon: 'wrench',
          title: 'Maintenance Mode',
          description: 'Enable system maintenance mode',
          type: 'toggle',
          value: adminData.systemSettings.general.maintenanceMode
        }
      ])}

      {renderSettingsSection('Security', [
        {
          icon: 'shield-check',
          title: 'Password Policy',
          description: 'Password strength requirements',
          type: 'value',
          value: adminData.systemSettings.security.passwordPolicy
        },
        {
          icon: 'two-factor-authentication',
          title: 'Two-Factor Authentication',
          description: 'Require 2FA for all users',
          type: 'toggle',
          value: adminData.systemSettings.security.twoFactorAuth
        },
        {
          icon: 'timer',
          title: 'Session Timeout',
          description: 'Auto-logout after inactivity',
          type: 'value',
          value: `${adminData.systemSettings.security.sessionTimeout} minutes`
        }
      ])}

      {renderSettingsSection('Integrations', [
        {
          icon: 'google',
          title: 'Google Workspace',
          description: 'Single sign-on and calendar integration',
          type: 'toggle',
          value: adminData.systemSettings.integrations.googleWorkspace.enabled
        },
        {
          icon: 'slack',
          title: 'Slack Integration',
          description: 'Notifications and communication',
          type: 'toggle',
          value: adminData.systemSettings.integrations.slack.enabled
        },
        {
          icon: 'video',
          title: 'Zoom Integration',
          description: 'Video conferencing for interviews',
          type: 'toggle',
          value: adminData.systemSettings.integrations.zoom.enabled
        }
      ])}

      {/* Per-Screen Password Management */}
      <View style={styles.sectionCard}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <MaterialCommunityIcons name="lock" size={22} color="#667eea" />
          <View>
            <Text style={styles.sectionTitle}>Screen Passwords</Text>
            <Text style={{ fontSize: 12, color: '#8E8E93', marginTop: 2 }}>Set a different password for each protected screen</Text>
          </View>
        </View>

        {Object.entries(SCREEN_LABELS).map(([sn, label]) => {
          const info = screenPasswords[sn] || {};
          const isActive = activeScreenPass === sn;
          return (
            <View key={sn} style={{ marginBottom: 12, backgroundColor: '#F8FAFC', borderRadius: 10, padding: 12, borderWidth: 1, borderColor: isActive ? '#667eea' : '#E2E8F0' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: '#1a1a2e' }}>{label}</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3 }}>
                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: info.exists ? '#10B981' : '#94A3B8' }} />
                    <Text style={{ fontSize: 11, color: info.exists ? '#10B981' : '#94A3B8' }}>{info.exists ? 'Password set' : 'No password'}</Text>
                  </View>
                </View>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <TouchableOpacity onPress={() => setActiveScreenPass(isActive ? null : sn)}
                    style={{ backgroundColor: '#667eea15', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 }}>
                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#667eea' }}>{info.exists ? 'Change' : 'Set'}</Text>
                  </TouchableOpacity>
                  {info.exists && (
                    <TouchableOpacity onPress={() => handleRemoveScreenPassword(sn)}
                      style={{ backgroundColor: '#FEF2F2', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 }}>
                      <Text style={{ fontSize: 12, fontWeight: '600', color: '#EF4444' }}>Remove</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
              {isActive && (
                <View style={{ marginTop: 12 }}>
                  <TextInput
                    style={{ backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, marginBottom: 8 }}
                    placeholder="New password (min 4 chars)"
                    value={screenPassInput}
                    onChangeText={setScreenPassInput}
                    secureTextEntry
                  />
                  <TextInput
                    style={{ backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, marginBottom: 8 }}
                    placeholder="Confirm password"
                    value={screenPassConfirm}
                    onChangeText={setScreenPassConfirm}
                    secureTextEntry
                  />
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <TouchableOpacity onPress={() => handleSetScreenPassword(sn)}
                      style={{ flex: 1, backgroundColor: '#667eea', borderRadius: 8, paddingVertical: 10, alignItems: 'center' }}>
                      <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>Save</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => { setActiveScreenPass(null); setScreenPassInput(''); setScreenPassConfirm(''); }}
                      style={{ flex: 1, backgroundColor: '#F1F5F9', borderRadius: 8, paddingVertical: 10, alignItems: 'center' }}>
                      <Text style={{ color: '#64748B', fontWeight: '600', fontSize: 13 }}>Cancel</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          );
        })}
      </View>

      {/* HR Permissions Control */}
      <View style={styles.sectionCard}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <MaterialCommunityIcons name="shield-lock" size={22} color="#667eea" />
          <View>
            <Text style={styles.sectionTitle}>HR Dashboard Permissions</Text>
            <Text style={{ fontSize: 12, color: '#8E8E93', marginTop: 2 }}>Control what HR staff can do</Text>
          </View>
        </View>

        {[
          { section: 'Employee Management', items: [
            { key: 'add_employee', label: 'Add Employees', desc: 'Create new employee records' },
            { key: 'edit_employee', label: 'Edit Employees', desc: 'Modify employee information' },
            { key: 'delete_employee', label: 'Delete / Terminate', desc: 'Remove or terminate employees' },
            { key: 'full_employee_mgmt', label: 'Full Employee Management', desc: 'Access full management screen' },
            { key: 'team_management', label: 'Team Management', desc: 'Access team view screen' },
            { key: 'bulk_actions', label: 'Bulk Actions', desc: 'Perform actions on multiple employees' },
          ]},
          { section: 'Leave & Attendance', items: [
            { key: 'approve_leave', label: 'Approve Leave', desc: 'Approve leave requests' },
            { key: 'reject_leave', label: 'Reject Leave', desc: 'Reject leave requests' },
          ]},
          { section: 'Payroll & Finance', items: [
            { key: 'process_payroll', label: 'Process Payroll', desc: 'Run payroll processing' },
            { key: 'view_payroll', label: 'View Payroll', desc: 'View payroll information' },
            { key: 'view_financials', label: 'Access Financials', desc: 'Navigate to financial screens' },
          ]},
          { section: 'Recruitment', items: [
            { key: 'post_job', label: 'Post Jobs', desc: 'Create and publish job listings' },
            { key: 'edit_job', label: 'Edit Jobs', desc: 'Modify existing job posts' },
          ]},
          { section: 'Disciplinary & Reviews', items: [
            { key: 'issue_warning', label: 'Issue Warnings', desc: 'Send warning letters' },
            { key: 'terminate_employee', label: 'Terminate Employee', desc: 'Process terminations' },
            { key: 'schedule_review', label: 'Schedule Reviews', desc: 'Schedule performance reviews' },
          ]},
          { section: 'Navigation & Access', items: [
            { key: 'visit_tracking', label: 'Visit Tracking', desc: 'Access visit tracking screen' },
            { key: 'follow_up_reminders', label: 'Follow-Up Reminders', desc: 'Access follow-up reminders' },
            { key: 'view_employee_details', label: 'View Employee Details', desc: 'Open individual employee profiles' },
            { key: 'navigate_payroll_mgmt', label: 'Payroll Management Screen', desc: 'Navigate to full payroll screen' },
            { key: 'navigate_recruitment', label: 'Recruitment Flow Screen', desc: 'Navigate to recruitment management' },
            { key: 'view_notifications', label: 'View Notifications', desc: 'Access notification center' },
          ]},
          { section: 'Disciplinary Extended', items: [
            { key: 'resolve_warnings', label: 'Resolve Warnings', desc: 'Mark warning letters as resolved' },
            { key: 'delete_warnings', label: 'Delete Warnings', desc: 'Permanently remove warning records' },
          ]},
          { section: 'Other', items: [
            { key: 'create_onboarding', label: 'Create Onboarding', desc: 'Set up new hire onboarding' },
            { key: 'create_benefit', label: 'Manage Benefits', desc: 'Create benefit plans' },
            { key: 'create_policy', label: 'Manage Policies', desc: 'Create company policies' },
            { key: 'create_exit_interview', label: 'Exit Interviews', desc: 'Create exit interview records' },
            { key: 'manage_training', label: 'Manage Training', desc: 'Schedule and manage training' },
            { key: 'export_reports', label: 'Export Reports', desc: 'Generate and export reports' },
            { key: 'view_analytics', label: 'View Analytics', desc: 'Access HR analytics' },
          ]},
        ].map((group) => (
          <View key={group.section} style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 14, fontWeight: '700', color: '#334155', marginBottom: 8 }}>{group.section}</Text>
            {group.items.map((item) => (
              <View key={item.key} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
                <View style={{ flex: 1, marginRight: 12 }}>
                  <Text style={{ fontSize: 14, color: '#1a1a2e', fontWeight: '500' }}>{item.label}</Text>
                  <Text style={{ fontSize: 11, color: '#94A3B8', marginTop: 1 }}>{item.desc}</Text>
                </View>
                <Switch
                  value={!!hrPermissions[item.key]}
                  onValueChange={() => togglePermission(item.key)}
                  trackColor={{ false: '#E2E8F0', true: '#667eea80' }}
                  thumbColor={hrPermissions[item.key] ? '#667eea' : '#fff'}
                />
              </View>
            ))}
          </View>
        ))}
      </View>

    </ScrollView>
  );

  const renderReportCard = ({ item }) => (
    <TouchableOpacity style={styles.reportCard} onPress={() => exportReport(item.name)}>
      <View style={styles.reportHeader}>
        <View style={styles.reportInfo}>
          <Text style={styles.reportName}>{item.name}</Text>
          <Text style={styles.reportDescription}>{item.description}</Text>
        </View>
        <View style={[styles.reportStatus, { backgroundColor: getStatusColor(item.status) + '20' }]}>
          <Text style={[styles.reportStatusText, { color: getStatusColor(item.status) }]}>
            {item.status}
          </Text>
        </View>
      </View>

      <View style={styles.reportDetails}>
        <View style={styles.reportDetail}>
          <MaterialCommunityIcons name="clock" size={16} color="#8E8E93" />
          <Text style={styles.reportDetailText}>Frequency: {item.frequency}</Text>
        </View>
        <View style={styles.reportDetail}>
          <MaterialCommunityIcons name="calendar" size={16} color="#8E8E93" />
          <Text style={styles.reportDetailText}>Next Run: {formatDateTime(item.nextRun)}</Text>
        </View>
        <View style={styles.reportDetail}>
          <MaterialCommunityIcons name="file-document" size={16} color="#8E8E93" />
          <Text style={styles.reportDetailText}>Format: {item.format}</Text>
        </View>
      </View>

      <View style={styles.reportFooter}>
        <Text style={styles.reportLastRun}>
          Last run: {formatDateTime(item.lastRun)} • {item.lastRunStatus}
        </Text>
        <TouchableOpacity style={styles.downloadButton} onPress={() => exportReport(item.name)}>
          <Ionicons name="download" size={16} color="#667eea" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderReports = () => (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
      <View style={styles.reportsSection}>
        <Text style={styles.sectionTitle}>Scheduled Reports</Text>
        <View style={styles.reportsList}>
          {adminData.reports.scheduled.map((report) => (
            <View key={report.id}>
              {renderReportCard({ item: report })}
            </View>
          ))}
        </View>
      </View>

      <View style={styles.reportsSection}>
        <Text style={styles.sectionTitle}>Custom Reports</Text>
        <View style={styles.reportsList}>
          {adminData.reports.custom.map((report) => (
            <TouchableOpacity key={report.id} style={styles.customReportCard}>
              <View style={styles.customReportHeader}>
                <View style={styles.customReportInfo}>
                  <Text style={styles.customReportName}>{report.name}</Text>
                  <Text style={styles.customReportDescription}>{report.description}</Text>
                </View>
                <View style={[styles.customReportStatus, { backgroundColor: getStatusColor(report.status) + '20' }]}>
                  <Text style={[styles.customReportStatusText, { color: getStatusColor(report.status) }]}>
                    {report.status}
                  </Text>
                </View>
              </View>
              <View style={styles.customReportFooter}>
                <Text style={styles.customReportCreated}>
                  Created by {report.createdBy} on {report.createdDate}
                </Text>
                <TouchableOpacity style={styles.downloadButton}>
                  <Ionicons name="download" size={16} color="#667eea" />
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  const renderAuditLogItem = ({ item }) => (
    <View style={styles.auditLogItem}>
      <View style={styles.auditLogHeader}>
        <View style={[
          styles.auditLogIcon,
          { backgroundColor: getSeverityColor(item.severity) + '20' }
        ]}>
          <MaterialCommunityIcons 
            name={
              item.action.includes('LOGIN') ? 'login' :
              item.action.includes('USER') ? 'account' :
              item.action.includes('REPORT') ? 'file-document' :
              item.action.includes('BACKUP') ? 'backup-restore' : 'information'
            } 
            size={16} 
            color={getSeverityColor(item.severity)} 
          />
        </View>
        <View style={styles.auditLogInfo}>
          <Text style={styles.auditLogAction}>{item.action}</Text>
          <Text style={styles.auditLogResource}>{item.resource}</Text>
        </View>
        <Text style={styles.auditLogTimestamp}>
          {formatDateTime(item.timestamp)}
        </Text>
      </View>
      <Text style={styles.auditLogDetails}>{item.details}</Text>
      <View style={styles.auditLogFooter}>
        <Text style={styles.auditLogUser}>User: {item.user}</Text>
        <Text style={styles.auditLogIP}>IP: {item.ipAddress}</Text>
      </View>
    </View>
  );

  const renderAudit = () => (
    <View style={styles.content}>
      <View style={styles.auditHeader}>
        <Text style={styles.auditTitle}>Audit Logs</Text>
        <TouchableOpacity
          style={styles.exportButton}
          onPress={() => exportReport('Audit Report')}
        >
          <MaterialCommunityIcons name="download" size={20} color="#667eea" />
          <Text style={styles.exportButtonText}>Export</Text>
        </TouchableOpacity>
      </View>

      <FlatList
        data={adminData.auditLogs}
        keyExtractor={(item) => item.id}
        renderItem={renderAuditLogItem}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      />
    </View>
  );


  // ── HR Tab ──
  const renderHR = () => (
    <ScrollView style={styles.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}>
      <View style={styles.sectionCard}>
        <Text style={styles.sectionTitle}>HR Overview</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
          {[
            { icon: 'people', label: 'Total Staff', value: adminData.users?.length || 0, color: '#667eea' },
            { icon: 'checkmark-circle', label: 'Active', value: adminData.users?.filter(u => u.status?.toLowerCase() === 'active').length || 0, color: '#10B981' },
            { icon: 'time', label: 'Pending', value: adminData.users?.filter(u => u.status?.toLowerCase() === 'pending').length || 0, color: '#F59E0B' },
            { icon: 'close-circle', label: 'Inactive', value: adminData.users?.filter(u => u.status?.toLowerCase() === 'inactive').length || 0, color: '#EF4444' },
          ].map((s, i) => (
            <View key={i} style={{ width: (width - 52) / 2, backgroundColor: '#fff', borderRadius: 12, padding: 16, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 }}>
              <Ionicons name={s.icon} size={24} color={s.color} />
              <Text style={{ fontSize: 22, fontWeight: 'bold', color: s.color, marginTop: 6 }}>{s.value}</Text>
              <Text style={{ fontSize: 11, color: '#8E8E93', marginTop: 2 }}>{s.label}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.sectionCard}>
        <Text style={styles.sectionTitle}>Departments</Text>
        {(() => {
          const depts = {};
          (adminData.users || []).forEach(u => { depts[u.department || 'General'] = (depts[u.department || 'General'] || 0) + 1; });
          const maxCount = Math.max(...Object.values(depts), 1);
          return Object.entries(depts).map(([dept, count]) => (
            <View key={dept} style={{ marginBottom: 12 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ fontSize: 14, color: '#1a1a2e' }}>{dept}</Text>
                <Text style={{ fontSize: 14, fontWeight: '600', color: '#667eea' }}>{count}</Text>
              </View>
              <View style={{ height: 6, backgroundColor: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
                <View style={{ height: '100%', width: `${(count / maxCount) * 100}%`, backgroundColor: '#667eea', borderRadius: 3 }} />
              </View>
            </View>
          ));
        })()}
      </View>

      <View style={styles.sectionCard}>
        <Text style={styles.sectionTitle}>Roles Distribution</Text>
        {(() => {
          const roles = {};
          (adminData.users || []).forEach(u => { roles[u.role || 'Staff'] = (roles[u.role || 'Staff'] || 0) + 1; });
          return Object.entries(roles).map(([role, count]) => (
            <View key={role} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
              <Text style={{ fontSize: 14, color: '#334155' }}>{role}</Text>
              <View style={{ backgroundColor: '#667eea20', paddingHorizontal: 10, paddingVertical: 2, borderRadius: 10 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#667eea' }}>{count}</Text>
              </View>
            </View>
          ));
        })()}
      </View>

      <TouchableOpacity
        onPress={() => exportReport('HR Summary')}
        style={{ backgroundColor: '#667eea', borderRadius: 12, padding: 14, alignItems: 'center', marginHorizontal: 16, marginBottom: 30, flexDirection: 'row', justifyContent: 'center', gap: 8 }}
      >
        <Ionicons name="download-outline" size={18} color="#fff" />
        <Text style={{ color: '#fff', fontWeight: '600', fontSize: 15 }}>Export HR Report</Text>
      </TouchableOpacity>
    </ScrollView>
  );

  // ── PDF / Print / Export ──
  const buildReportHTML = (title, sections) => {
    const cn = companyName || 'Company';
    const logoHTML = companyLogo ? `<img src="${companyLogo}" style="width:50px;height:50px;border-radius:8px;" />` : '';
    const sectionsHTML = sections.map(s =>
      `<div style="margin-bottom:16px"><h3 style="font-size:15px;color:#667eea;border-bottom:1px solid #eee;padding-bottom:4px">${s.title}</h3>${s.html}</div>`
    ).join('');
    return `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>
*{box-sizing:border-box;margin:0;padding:0}body{font-family:-apple-system,sans-serif;color:#222;padding:28px}
.hdr{display:flex;align-items:center;gap:14px;border-bottom:2px solid #667eea;padding-bottom:14px;margin-bottom:18px}
.hdr h1{font-size:20px;color:#667eea}.hdr p{font-size:12px;color:#666}
table{width:100%;border-collapse:collapse;margin:8px 0}th,td{padding:7px 10px;border-bottom:1px solid #eee;font-size:12px;text-align:left}
th{background:#f8f9fa;color:#555;font-weight:600}
.ft{margin-top:20px;text-align:center;font-size:10px;color:#aaa;border-top:1px solid #eee;padding-top:8px}
</style></head><body>
<div class="hdr">${logoHTML}<div><h1>${title}</h1><p>${cn} · Generated ${new Date().toLocaleDateString('en-GB')}</p></div></div>
${sectionsHTML}
<div class="ft">${cn} · Powered by JOBENG</div>
</body></html>`;
  };

  const exportReport = async (type) => {
    try {
      let sections = [];
      if (type === 'HR Summary') {
        const depts = {};
        (adminData.users || []).forEach(u => { depts[u.department || 'General'] = (depts[u.department || 'General'] || 0) + 1; });
        const total = adminData.users?.length || 0;
        const active = adminData.users?.filter(u => u.status?.toLowerCase() === 'active').length || 0;
        sections = [
          { title: 'Overview', html: `<table><tr><td>Total Staff</td><td><b>${total}</b></td></tr><tr><td>Active</td><td><b>${active}</b></td></tr><tr><td>Departments</td><td><b>${Object.keys(depts).length}</b></td></tr></table>` },
          { title: 'Departments', html: `<table><thead><tr><th>Department</th><th>Count</th></tr></thead><tbody>${Object.entries(depts).map(([d, c]) => `<tr><td>${d}</td><td>${c}</td></tr>`).join('')}</tbody></table>` },
          { title: 'Staff List', html: `<table><thead><tr><th>Name</th><th>Role</th><th>Dept</th><th>Status</th></tr></thead><tbody>${(adminData.users || []).slice(0, 50).map(u => `<tr><td>${u.name}</td><td>${u.role}</td><td>${u.department}</td><td>${u.status}</td></tr>`).join('')}</tbody></table>` },
        ];
      } else if (type === 'User Report') {
        sections = [
          { title: 'All Users', html: `<table><thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Department</th><th>Status</th></tr></thead><tbody>${(adminData.users || []).map(u => `<tr><td>${u.name}</td><td>${u.email}</td><td>${u.role}</td><td>${u.department}</td><td>${u.status}</td></tr>`).join('')}</tbody></table>` },
        ];
      } else if (type === 'Audit Report') {
        sections = [
          { title: 'Recent Activity', html: `<table><thead><tr><th>Time</th><th>Type</th><th>Description</th><th>User</th></tr></thead><tbody>${(adminData.dashboard?.recentActivity || []).map(a => `<tr><td>${a.timestamp}</td><td>${a.type}</td><td>${a.description}</td><td>${a.user}</td></tr>`).join('')}</tbody></table>` },
          { title: 'System Alerts', html: `<table><thead><tr><th>Time</th><th>Severity</th><th>Title</th><th>Message</th></tr></thead><tbody>${(adminData.systemAlerts || []).map(a => `<tr><td>${a.timestamp}</td><td>${a.severity}</td><td>${a.title}</td><td>${a.message}</td></tr>`).join('')}</tbody></table>` },
        ];
      } else {
        sections = [{ title: 'System Overview', html: `<table><tr><td>Total Users</td><td>${adminData.dashboard?.quickStats?.totalUsers || 0}</td></tr><tr><td>Active</td><td>${adminData.dashboard?.systemHealth?.activeUsers || 0}</td></tr><tr><td>Uptime</td><td>${adminData.dashboard?.systemHealth?.uptime || '-'}</td></tr></table>` }];
      }
      const html = buildReportHTML(type, sections);
      try {
        const { uri } = await Print.printToFileAsync({ html, base64: false });
        const available = await Sharing.isAvailableAsync();
        if (available) { await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle: type, UTI: 'com.adobe.pdf' }); return; }
      } catch (fe) { console.warn('printToFileAsync unavailable:', fe?.message); }
      await Print.printAsync({ html });
    } catch (e) { Alert.alert('Error', 'Export failed'); }
  };

  const printReport = async (type) => {
    try { await exportReport(type); } catch { Alert.alert('Error', 'Print failed'); }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'users':
        return renderUsers();
      case 'hr':
        return renderHR();
      case 'settings':
        return renderSettings();
      case 'reports':
        return renderReports();
      case 'audit':
        return renderAudit();
      default:
        return renderDashboard();
    }
  };

  const renderSystemAlertsModal = () => (
    <Modal
      visible={showModal && modalType === 'systemAlerts'}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowModal(false)}
          >
            <Ionicons name="close" size={24} color="#8E8E93" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>System Alerts</Text>
          <TouchableOpacity
            style={styles.modalActionButton}
            onPress={() => Alert.alert('Clear All', 'Clear all alerts?')}
          >
            <Text style={styles.modalActionText}>Clear All</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
          <View style={styles.alertsList}>
            {adminData.systemAlerts && adminData.systemAlerts.map((alert) => (
              <View key={alert.id} style={styles.alertItem}>
                <View style={[styles.alertIcon, { backgroundColor: getSeverityColor(alert.severity) + '20' }]}>
                  <MaterialCommunityIcons 
                    name={alert.severity === 'critical' ? 'alert' : alert.severity === 'warning' ? 'alert-outline' : 'information'}
                    size={20} 
                    color={getSeverityColor(alert.severity)} 
                  />
                </View>
                <View style={styles.alertContent}>
                  <Text style={styles.alertTitle}>{alert.title}</Text>
                  <Text style={styles.alertMessage}>{alert.message}</Text>
                  <Text style={styles.alertTimestamp}>{formatDateTime(alert.timestamp)}</Text>
                </View>
                <TouchableOpacity style={styles.alertDismiss}>
                  <Ionicons name="close" size={16} color="#8E8E93" />
                </TouchableOpacity>
              </View>
            ))}
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderAllActivityModal = () => (
    <Modal
      visible={showModal && modalType === 'allActivity'}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowModal(false)}
          >
            <Ionicons name="close" size={24} color="#8E8E93" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>All Activity</Text>
          <TouchableOpacity
            style={styles.modalActionButton}
            onPress={() => exportReport('Audit Report')}
          >
            <Text style={styles.modalActionText}>Export</Text>
          </TouchableOpacity>
        </View>

        <FlatList
          data={adminData.dashboard.recentActivity}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <View style={styles.activityItem}>
              <View style={[styles.activityIcon, { backgroundColor: getSeverityColor(item.severity) + '20' }]}>
                <MaterialCommunityIcons 
                  name={
                    item.type === 'user_created' ? 'account-plus' :
                    item.type === 'system_alert' ? 'alert' :
                    item.type === 'data_export' ? 'download' :
                    item.type === 'security_event' ? 'shield-alert' : 'information'
                  } 
                  size={16} 
                  color={getSeverityColor(item.severity)} 
                />
              </View>
              <View style={styles.activityContent}>
                <Text style={styles.activityDescription}>{item.description}</Text>
                <Text style={styles.activityTimestamp}>
                  {formatDateTime(item.timestamp)} • {item.user}
                </Text>
              </View>
            </View>
          )}
          style={styles.modalBody}
        />
      </SafeAreaView>
    </Modal>
  );

  const renderCreateUserModal = () => (
    <Modal
      visible={showModal && modalType === 'createUser'}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowModal(false)}
          >
            <Ionicons name="close" size={24} color="#8E8E93" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Create New User</Text>
          <TouchableOpacity
            style={styles.modalActionButton}
            onPress={() => {
              Alert.alert('Success', 'User created successfully!');
              setShowModal(false);
            }}
          >
            <Text style={styles.modalActionText}>Create</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Basic Information</Text>
            
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Full Name *</Text>
              <TextInput
                style={styles.formInput}
                placeholder="Enter full name"
                placeholderTextColor="#8E8E93"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Email Address *</Text>
              <TextInput
                style={styles.formInput}
                placeholder="Enter email address"
                placeholderTextColor="#8E8E93"
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Role *</Text>
              <TextInput
                style={styles.formInput}
                placeholder="Select role (Intern, Mentor, HR Manager, Admin)"
                placeholderTextColor="#8E8E93"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Department *</Text>
              <TextInput
                style={styles.formInput}
                placeholder="Enter department"
                placeholderTextColor="#8E8E93"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Phone Number</Text>
              <TextInput
                style={styles.formInput}
                placeholder="Enter phone number"
                placeholderTextColor="#8E8E93"
                keyboardType="phone-pad"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Location</Text>
              <TextInput
                style={styles.formInput}
                placeholder="Enter location"
                placeholderTextColor="#8E8E93"
              />
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderUserFiltersModal = () => (
    <Modal
      visible={showModal && modalType === 'userFilters'}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowModal(false)}
          >
            <Ionicons name="close" size={24} color="#8E8E93" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Filter Users</Text>
          <TouchableOpacity
            style={styles.modalActionButton}
            onPress={() => {
              setFilters({ department: 'All', status: 'All', role: 'All' });
              setShowModal(false);
            }}
          >
            <Text style={styles.modalActionText}>Reset</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Department</Text>
            {['All', 'Engineering', 'Design', 'Marketing', 'HR', 'Finance'].map((dept) => (
              <TouchableOpacity
                key={dept}
                style={[styles.filterOption, filters.department === dept && styles.filterOptionActive]}
                onPress={() => setFilters({...filters, department: dept})}
              >
                <Text style={[styles.filterOptionText, filters.department === dept && styles.filterOptionTextActive]}>
                  {dept}
                </Text>
                {filters.department === dept && (
                  <Ionicons name="checkmark" size={16} color="#667eea" />
                )}
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Status</Text>
            {['All', 'Active', 'Inactive', 'Pending'].map((status) => (
              <TouchableOpacity
                key={status}
                style={[styles.filterOption, filters.status === status && styles.filterOptionActive]}
                onPress={() => setFilters({...filters, status: status})}
              >
                <Text style={[styles.filterOptionText, filters.status === status && styles.filterOptionTextActive]}>
                  {status}
                </Text>
                {filters.status === status && (
                  <Ionicons name="checkmark" size={16} color="#667eea" />
                )}
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Role</Text>
            {['All', 'Admin', 'HR Manager', 'Mentor', 'Intern'].map((role) => (
              <TouchableOpacity
                key={role}
                style={[styles.filterOption, filters.role === role && styles.filterOptionActive]}
                onPress={() => setFilters({...filters, role: role})}
              >
                <Text style={[styles.filterOptionText, filters.role === role && styles.filterOptionTextActive]}>
                  {role}
                </Text>
                {filters.role === role && (
                  <Ionicons name="checkmark" size={16} color="#667eea" />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderUserDetailsModal = () => (
    <Modal
      visible={showModal && modalType === 'userDetails'}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowModal(false)}
          >
            <Ionicons name="close" size={24} color="#8E8E93" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>User Details</Text>
          <TouchableOpacity
            style={styles.modalActionButton}
            onPress={() => Alert.alert('Edit User', 'Edit user functionality')}
          >
            <Text style={styles.modalActionText}>Edit</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
          {selectedItem && (
            <View style={styles.userDetailsContainer}>
              <View style={styles.userDetailsHeader}>
                <Image source={{ uri: (selectedItem.avatar && selectedItem.avatar.startsWith('http')) ? selectedItem.avatar : undefined }} style={styles.userDetailsAvatar} />
                <View style={styles.userDetailsInfo}>
                  <Text style={styles.userDetailsName}>{selectedItem.name}</Text>
                  <Text style={styles.userDetailsEmail}>{selectedItem.email}</Text>
                  <View style={styles.userDetailsRole}>
                    <Text style={styles.userDetailsRoleText}>{selectedItem.role}</Text>
                    <Text style={styles.userDetailsDepartment}> • {selectedItem.department}</Text>
                  </View>
                </View>
              </View>

              <View style={styles.userDetailsSection}>
                <Text style={styles.userDetailsSectionTitle}>Contact Information</Text>
                <View style={styles.userDetailsItem}>
                  <MaterialCommunityIcons name="phone" size={20} color="#667eea" />
                  <Text style={styles.userDetailsItemText}>{selectedItem.phone}</Text>
                </View>
                <View style={styles.userDetailsItem}>
                  <MaterialCommunityIcons name="map-marker" size={20} color="#667eea" />
                  <Text style={styles.userDetailsItemText}>{selectedItem.location}</Text>
                </View>
              </View>

              <View style={styles.userDetailsSection}>
                <Text style={styles.userDetailsSectionTitle}>Account Information</Text>
                <View style={styles.userDetailsItem}>
                  <MaterialCommunityIcons name="calendar" size={20} color="#667eea" />
                  <Text style={styles.userDetailsItemText}>
                    Joined: {formatDateTime(selectedItem.joinDate)}
                  </Text>
                </View>
                <View style={styles.userDetailsItem}>
                  <MaterialCommunityIcons name="clock" size={20} color="#667eea" />
                  <Text style={styles.userDetailsItemText}>
                    Last login: {formatDateTime(selectedItem.lastLogin)}
                  </Text>
                </View>
                <View style={styles.userDetailsItem}>
                  <MaterialCommunityIcons name="shield-check" size={20} color="#667eea" />
                  <Text style={styles.userDetailsItemText}>
                    Status: {selectedItem.status}
                  </Text>
                </View>
              </View>

              <View style={styles.userDetailsSection}>
                <Text style={styles.userDetailsSectionTitle}>Permissions</Text>
                <View style={styles.permissionsList}>
                  {selectedItem.permissions && selectedItem.permissions.map((permission, index) => (
                    <View key={index} style={styles.permissionItem}>
                      <MaterialCommunityIcons name="check-circle" size={16} color="#10B981" />
                      <Text style={styles.permissionText}>{permission}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderModal = () => {
    switch (modalType) {
      case 'userDetails':
        return renderUserDetailsModal();
      case 'createUser':
        return renderCreateUserModal();
      case 'userFilters':
        return renderUserFiltersModal();
      case 'systemAlerts':
        return renderSystemAlertsModal();
      case 'allActivity':
        return renderAllActivityModal();
      case 'changePassword':
        return (
          <Modal visible={showModal && modalType === 'changePassword'} animationType="slide" presentationStyle="pageSheet">
            <SafeAreaView style={styles.modalContainer}>
              <View style={styles.modalHeader}>
                <TouchableOpacity onPress={() => { setShowModal(false); setPasswordError(''); }}>
                  <Ionicons name="close" size={24} color="#8E8E93" />
                </TouchableOpacity>
                <Text style={styles.modalTitle}>{hasPassword ? 'Change Password' : 'Set Password'}</Text>
                <View style={{ width: 24 }} />
              </View>
              <ScrollView style={{ padding: 20 }}>
                {hasPassword && (
                  <View style={{ marginBottom: 16 }}>
                    <Text style={{ fontSize: 13, fontWeight: '500', color: '#64748B', marginBottom: 6 }}>Current Password</Text>
                    <TextInput
                      style={{ backgroundColor: '#F8FAFC', borderRadius: 10, borderWidth: 1, borderColor: '#E2E8F0', paddingHorizontal: 14, paddingVertical: 12, fontSize: 15 }}
                      placeholder="Enter current password"
                      value={enteredPassword}
                      onChangeText={setEnteredPassword}
                      secureTextEntry
                    />
                  </View>
                )}
                <View style={{ marginBottom: 16 }}>
                  <Text style={{ fontSize: 13, fontWeight: '500', color: '#64748B', marginBottom: 6 }}>New Password</Text>
                  <TextInput
                    style={{ backgroundColor: '#F8FAFC', borderRadius: 10, borderWidth: 1, borderColor: '#E2E8F0', paddingHorizontal: 14, paddingVertical: 12, fontSize: 15 }}
                    placeholder="Min 4 characters"
                    value={newPassword}
                    onChangeText={setNewPassword}
                    secureTextEntry
                  />
                </View>
                <View style={{ marginBottom: 16 }}>
                  <Text style={{ fontSize: 13, fontWeight: '500', color: '#64748B', marginBottom: 6 }}>Confirm New Password</Text>
                  <TextInput
                    style={{ backgroundColor: '#F8FAFC', borderRadius: 10, borderWidth: 1, borderColor: '#E2E8F0', paddingHorizontal: 14, paddingVertical: 12, fontSize: 15 }}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChangeText={setConfirmPassword}
                    secureTextEntry
                  />
                </View>
                {passwordError ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 16, backgroundColor: '#FEF2F2', padding: 12, borderRadius: 10 }}>
                    <Ionicons name="alert-circle" size={16} color="#EF4444" />
                    <Text style={{ fontSize: 13, color: '#EF4444' }}>{passwordError}</Text>
                  </View>
                ) : null}
                <TouchableOpacity
                  onPress={async () => {
                    if (hasPassword) {
                      await changePassword();
                    } else {
                      await setupPassword();
                    }
                    if (!passwordError) setShowModal(false);
                  }}
                  style={{ backgroundColor: '#667eea', borderRadius: 12, paddingVertical: 16, alignItems: 'center' }}>
                  <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>{hasPassword ? 'Change Password' : 'Set Password'}</Text>
                </TouchableOpacity>
              </ScrollView>
            </SafeAreaView>
          </Modal>
        );
      default:
        return null;
    }
  };

  // ── Auth checking spinner ──
  if (authChecking) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F8F9FA' }}>
        <ActivityIndicator size={36} color="#667eea" />
        <Text style={{ color: '#8E8E93', marginTop: 12 }}>Verifying access...</Text>
      </View>
    );
  }

  // ── Password Gate Screen ──
  if (isAuthorized && !passwordVerified) {
    const isLocked = lockoutUntil && Date.now() < lockoutUntil;
    const lockMins = isLocked ? Math.ceil((lockoutUntil - Date.now()) / 60000) : 0;

    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8F9FA' }}>
        <StatusBar barStyle="dark-content" />
        <LinearGradient colors={['#667eea', '#764ba2']} style={{ paddingVertical: 20, paddingHorizontal: 20, alignItems: 'center' }}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={{ position: 'absolute', left: 16, top: 20 }}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <View style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <Ionicons name={passwordMode === 'setup' ? 'key' : 'lock-closed'} size={32} color="#fff" />
          </View>
          <Text style={{ fontSize: 22, fontWeight: 'bold', color: '#fff' }}>
            <Text>{passwordMode === 'setup' ? 'Set Admin Password' : 'Enter Admin Password'}</Text>
          </Text>
          <Text style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginTop: 4, textAlign: 'center' }}>
            {passwordMode === 'setup' ? 'Create a password to protect the admin panel' : 'This area is password protected'}
          </Text>
        </LinearGradient>

        <ScrollView contentContainerStyle={{ padding: 24 }}>
          {isLocked ? (
            <View style={{ alignItems: 'center', paddingVertical: 40 }}>
              <Ionicons name="time" size={48} color="#EF4444" />
              <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#EF4444', marginTop: 12 }}>Account Locked</Text>
              <Text style={{ fontSize: 14, color: '#64748B', textAlign: 'center', marginTop: 8 }}>
                Too many failed attempts. Try again in {lockMins} minute{lockMins > 1 ? 's' : ''}.
              </Text>
              <TouchableOpacity onPress={() => navigation.goBack()}
                style={{ marginTop: 24, backgroundColor: '#667eea', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 32 }}>
                <Text style={{ color: '#fff', fontWeight: '600', fontSize: 15 }}>Go Back</Text>
              </TouchableOpacity>
            </View>
          ) : passwordMode === 'setup' ? (
            <View>
              <Text style={{ fontSize: 15, fontWeight: '600', color: '#334155', marginBottom: 16 }}>Create your admin password</Text>

              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 13, fontWeight: '500', color: '#64748B', marginBottom: 6 }}>New Password *</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: '#E2E8F0', paddingHorizontal: 14 }}>
                  <Ionicons name="lock-closed" size={18} color="#94A3B8" />
                  <TextInput
                    style={{ flex: 1, paddingVertical: 14, paddingHorizontal: 10, fontSize: 16, color: '#222' }}
                    placeholder="Min 4 characters"
                    value={newPassword}
                    onChangeText={setNewPassword}
                    secureTextEntry={!showPassword}
                    autoFocus
                  />
                  <TouchableOpacity onPress={() => setShowPassword(v => !v)}>
                    <Ionicons name={showPassword ? 'eye-off' : 'eye'} size={20} color="#94A3B8" />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 13, fontWeight: '500', color: '#64748B', marginBottom: 6 }}>Confirm Password *</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: '#E2E8F0', paddingHorizontal: 14 }}>
                  <Ionicons name="lock-closed" size={18} color="#94A3B8" />
                  <TextInput
                    style={{ flex: 1, paddingVertical: 14, paddingHorizontal: 10, fontSize: 16, color: '#222' }}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChangeText={setConfirmPassword}
                    secureTextEntry={!showPassword}
                  />
                </View>
              </View>

              {passwordError ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 16, backgroundColor: '#FEF2F2', padding: 12, borderRadius: 10 }}>
                  <Ionicons name="alert-circle" size={16} color="#EF4444" />
                  <Text style={{ fontSize: 13, color: '#EF4444', flex: 1 }}>{passwordError}</Text>
                </View>
              ) : null}

              <TouchableOpacity onPress={setupPassword}
                style={{ backgroundColor: '#667eea', borderRadius: 12, paddingVertical: 16, alignItems: 'center' }}>
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>Set Password & Enter</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View>
              <Text style={{ fontSize: 15, fontWeight: '600', color: '#334155', marginBottom: 16 }}>Enter admin password to continue</Text>

              <View style={{ marginBottom: 16 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: passwordError ? '#EF4444' : '#E2E8F0', paddingHorizontal: 14 }}>
                  <Ionicons name="lock-closed" size={18} color={passwordError ? '#EF4444' : '#94A3B8'} />
                  <TextInput
                    style={{ flex: 1, paddingVertical: 14, paddingHorizontal: 10, fontSize: 16, color: '#222' }}
                    placeholder="Enter password"
                    value={enteredPassword}
                    onChangeText={(t) => { setEnteredPassword(t); setPasswordError(''); }}
                    secureTextEntry={!showPassword}
                    autoFocus
                    onSubmitEditing={verifyPassword}
                    returnKeyType="go"
                  />
                  <TouchableOpacity onPress={() => setShowPassword(v => !v)}>
                    <Ionicons name={showPassword ? 'eye-off' : 'eye'} size={20} color="#94A3B8" />
                  </TouchableOpacity>
                </View>
              </View>

              {passwordError ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 16, backgroundColor: '#FEF2F2', padding: 12, borderRadius: 10 }}>
                  <Ionicons name="alert-circle" size={16} color="#EF4444" />
                  <Text style={{ fontSize: 13, color: '#EF4444', flex: 1 }}>{passwordError}</Text>
                </View>
              ) : null}

              {passwordAttempts > 0 && (
                <Text style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', marginBottom: 12 }}>
                  {MAX_ATTEMPTS - passwordAttempts} attempt{MAX_ATTEMPTS - passwordAttempts > 1 ? 's' : ''} remaining before lockout
                </Text>
              )}

              <TouchableOpacity onPress={verifyPassword}
                style={{ backgroundColor: '#667eea', borderRadius: 12, paddingVertical: 16, alignItems: 'center', marginBottom: 12 }}>
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>Unlock</Text>
              </TouchableOpacity>

              <TouchableOpacity onPress={() => navigation.goBack()}
                style={{ borderRadius: 12, paddingVertical: 14, alignItems: 'center', borderWidth: 1, borderColor: '#E2E8F0' }}>
                <Text style={{ color: '#64748B', fontWeight: '600', fontSize: 15 }}>Go Back</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </SafeAreaView>
    );
  }

  // ── Unauthorized screen ──
  if (!isAuthorized) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8F9FA' }}>
        <StatusBar barStyle="dark-content" />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 30 }}>
          <View style={{ width: 80, height: 80, borderRadius: 40, backgroundColor: '#EF444420', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <Ionicons name="lock-closed" size={40} color="#EF4444" />
          </View>
          <Text style={{ fontSize: 22, fontWeight: 'bold', color: '#1a1a2e', marginBottom: 8 }}>Access Restricted</Text>
          <Text style={{ fontSize: 15, color: '#64748B', textAlign: 'center', lineHeight: 22, marginBottom: 6 }}>
            This screen is for authorized administrators and HR managers only.
          </Text>
          {currentUser ? (
            <Text style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', marginBottom: 20 }}>
              Signed in as: {currentUser.full_name || currentUser.name || currentUser.email || 'Unknown'}{'\n'}
              Role: {currentUser.user_role || currentUser.role || 'Not assigned'}
            </Text>
          ) : (
            <Text style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', marginBottom: 20 }}>
              No user session found. Please sign in first.
            </Text>
          )}
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#667eea', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 24, gap: 8 }}
            >
              <Ionicons name="arrow-back" size={18} color="#fff" />
              <Text style={{ color: '#fff', fontWeight: '600', fontSize: 15 }}>Go Back</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => checkAuthAndInit()}
              style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#F0F4FF', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 24, gap: 8, borderWidth: 1, borderColor: '#667eea' }}
            >
              <Ionicons name="refresh" size={18} color="#667eea" />
              <Text style={{ color: '#667eea', fontWeight: '600', fontSize: 15 }}>Retry</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  // ── Data loading spinner ──
  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F8F9FA' }}>
        <ActivityIndicator size={36} color="#667eea" />
        <Text style={{ color: '#8E8E93', marginTop: 12 }}>Loading admin data...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {renderHeader()}
      {renderTabBar()}
      {renderContent()}
      
      {renderModal()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA'
  },
  header: {
    paddingBottom: 20
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 10
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center'
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF'
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2
  },
  alertButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center'
  },
  alertBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#EF4444',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center'
  },
  alertBadgeText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold'
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    paddingVertical: 15,
    paddingHorizontal: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA'
  },
  tabItem: {
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 5,
    borderRadius: 8
  },
  activeTab: {
    backgroundColor: '#F0F4FF'
  },
  tabText: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 4,
    textAlign: 'center'
  },
  activeTabText: {
    color: '#667eea',
    fontWeight: '600'
  },
  content: {
    flex: 1,
    padding: 20
  },
  systemHealthCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: 15
  },
  healthMetricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  healthMetric: {
    width: '48%',
    alignItems: 'center',
    padding: 15,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    marginBottom: 10
  },
  healthMetricValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginTop: 8
  },
  healthMetricLabel: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 4
  },
  healthDetails: {
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#E5E5EA'
  },
  healthDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8
  },
  healthDetailText: {
    fontSize: 14,
    color: '#8E8E93',
    marginLeft: 8
  },
  quickStatsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  quickStatsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  quickStat: {
    width: '48%',
    alignItems: 'center',
    padding: 15,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    marginBottom: 10
  },
  quickStatValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginTop: 8
  },
  quickStatLabel: {
    fontSize: 11,
    color: '#8E8E93',
    marginTop: 4,
    textAlign: 'center'
  },
  recentActivityCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  activityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15
  },
  viewAllButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: '#F0F4FF',
    borderRadius: 6
  },
  viewAllButtonText: {
    fontSize: 12,
    color: '#667eea',
    fontWeight: '600'
  },
  activityList: {
    marginTop: 10
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0'
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12
  },
  activityContent: {
    flex: 1
  },
  activityDescription: {
    fontSize: 14,
    color: '#1A1A1A',
    lineHeight: 20
  },
  activityTimestamp: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 4
  },
  usersHeader: {
    marginBottom: 20
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    paddingHorizontal: 15,
    paddingVertical: 12,
    marginRight: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1A1A1A',
    marginLeft: 10
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2
  },
  addUserButton: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: '#667eea',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2
  },
  userCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  userHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12
  },
  userAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 12
  },
  userInfo: {
    flex: 1
  },
  userName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1A1A1A'
  },
  userEmail: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 2
  },
  userMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4
  },
  userRole: {
    fontSize: 12,
    color: '#667eea',
    fontWeight: '600'
  },
  userDepartment: {
    fontSize: 12,
    color: '#8E8E93'
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600'
  },
  userStats: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12
  },
  userStat: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16
  },
  userStatText: {
    fontSize: 12,
    color: '#8E8E93',
    marginLeft: 4
  },
  userFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0'
  },
  userLocation: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  userLocationText: {
    fontSize: 12,
    color: '#8E8E93',
    marginLeft: 4
  },
  userLastLogin: {
    fontSize: 12,
    color: '#8E8E93'
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#8E8E93',
    marginTop: 16
  },
  emptyStateText: {
    fontSize: 14,
    color: '#C7C7CC',
    marginTop: 8,
    textAlign: 'center'
  },
  settingsSection: {
    marginBottom: 30
  },
  settingsSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: 15
  },
  settingsItems: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  settingsItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0'
  },
  settingsItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1
  },
  settingsItemInfo: {
    marginLeft: 12,
    flex: 1
  },
  settingsItemTitle: {
    fontSize: 16,
    color: '#1A1A1A',
    fontWeight: '500'
  },
  settingsItemDescription: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 2
  },
  settingsItemRight: {
    alignItems: 'center'
  },
  settingsItemValue: {
    fontSize: 14,
    color: '#8E8E93'
  },
  reportsSection: {
    marginBottom: 30
  },
  reportsList: {
    marginTop: 10
  },
  reportCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  reportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12
  },
  reportInfo: {
    flex: 1,
    marginRight: 12
  },
  reportName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1A1A1A'
  },
  reportDescription: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 4
  },
  reportStatus: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6
  },
  reportStatusText: {
    fontSize: 12,
    fontWeight: '600'
  },
  reportDetails: {
    marginBottom: 12
  },
  reportDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6
  },
  reportDetailText: {
    fontSize: 14,
    color: '#8E8E93',
    marginLeft: 8
  },
  reportFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0'
  },
  reportLastRun: {
    fontSize: 12,
    color: '#8E8E93'
  },
  downloadButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: '#F0F4FF'
  },
  customReportCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  customReportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12
  },
  customReportInfo: {
    flex: 1,
    marginRight: 12
  },
  customReportName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1A1A1A'
  },
  customReportDescription: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 4
  },
  customReportStatus: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6
  },
  customReportStatusText: {
    fontSize: 12,
    fontWeight: '600'
  },
  customReportFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0'
  },
  customReportCreated: {
    fontSize: 12,
    color: '#8E8E93'
  },
  auditHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20
  },
  auditTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1A1A1A'
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#F0F4FF',
    borderRadius: 8
  },
  exportButtonText: {
    fontSize: 14,
    color: '#667eea',
    fontWeight: '600',
    marginLeft: 6
  },
  auditLogItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  auditLogHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8
  },
  auditLogIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12
  },
  auditLogInfo: {
    flex: 1
  },
  auditLogAction: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1A1A1A'
  },
  auditLogResource: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 2
  },
  auditLogTimestamp: {
    fontSize: 12,
    color: '#8E8E93'
  },
  auditLogDetails: {
    fontSize: 14,
    color: '#1A1A1A',
    marginBottom: 8,
    lineHeight: 20
  },
  auditLogFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0'
  },
  auditLogUser: {
    fontSize: 12,
    color: '#8E8E93'
  },
  auditLogIP: {
    fontSize: 12,
    color: '#8E8E93'
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8F9FA'
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA'
  },
  modalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1A1A1A'
  },
  modalActionButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#667eea',
    borderRadius: 8
  },
  modalActionText: {
    fontSize: 16,
    color: '#FFFFFF',
    fontWeight: '600'
  },
  modalBody: {
    flex: 1,
    padding: 20
  },
  alertsList: {
    marginTop: 10
  },
  alertItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  alertIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12
  },
  alertContent: {
    flex: 1
  },
  alertTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: 4
  },
  alertMessage: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
    marginBottom: 8
  },
  alertTimestamp: {
    fontSize: 12,
    color: '#C7C7CC'
  },
  alertDismiss: {
    padding: 4
  },
  formSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  formSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: 15
  },
  formGroup: {
    marginBottom: 16
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1A1A1A',
    marginBottom: 8
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: '#1A1A1A',
    backgroundColor: '#FFFFFF'
  },
  filterSection: {
    marginBottom: 24
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: 12
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginBottom: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2
  },
  filterOptionActive: {
    backgroundColor: '#F0F4FF',
    borderColor: '#667eea',
    borderWidth: 1
  },
  filterOptionText: {
    fontSize: 16,
    color: '#1A1A1A'
  },
  filterOptionTextActive: {
    color: '#667eea',
    fontWeight: '600'
  },
  userDetailsContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  userDetailsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0'
  },
  userDetailsAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    marginRight: 16
  },
  userDetailsInfo: {
    flex: 1
  },
  userDetailsName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1A1A1A'
  },
  userDetailsEmail: {
    fontSize: 16,
    color: '#8E8E93',
    marginTop: 4
  },
  userDetailsRole: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8
  },
  userDetailsRoleText: {
    fontSize: 14,
    color: '#667eea',
    fontWeight: '600'
  },
  userDetailsDepartment: {
    fontSize: 14,
    color: '#8E8E93'
  },
  userDetailsSection: {
    marginBottom: 24
  },
  userDetailsSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: 12
  },
  userDetailsItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8
  },
  userDetailsItemText: {
    fontSize: 14,
    color: '#1A1A1A',
    marginLeft: 12
  },
  permissionsList: {
    marginTop: 8
  },
  permissionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6
  },
  permissionText: {
    fontSize: 14,
    color: '#1A1A1A',
    marginLeft: 8
  }
});

export default AdminHRManagementScreen;